'use client';

import { useCallback, useState } from 'react';

import type { RichOrderItem } from '@ps/api/types';

import type { OrderMode } from '../../../orders/types';
import type { ChatMessageAPI } from '../../hooks/useChatMessageAPI';
import { ChatMessageType } from '../../types/ChatMessage';
import type { ChatSessionType } from '../../types/ChatSessionType';
import type { ChatUser } from '../../types/ChatUser';
import { orderItemToOrderContent } from './functions/orderItemToOrderContent';

interface Params {
  sendMessage: ChatMessageAPI['sendMessage'];
  sessionType: ChatSessionType;
  currentUser: ChatUser;
  targetUser: ChatUser;
  role: OrderMode;
}

export interface ShareOrder {
  isPickerOpen: boolean;
  openPicker: () => void;
  closePicker: () => void;
  shareOrders: (orders: RichOrderItem[]) => void;
}

/**
 * "Share Order" handler. Owns the picker's open state and, on confirm,
 * sends one `Order` message per selected order (1:1 with PopshopRN's
 * `shareOrder` → `onShare` loop). Order content is pure metadata, so no
 * upload step is involved.
 */
export function useShareOrder({
  sendMessage,
  sessionType,
  currentUser,
  targetUser,
  role,
}: Params): ShareOrder {
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const openPicker = useCallback(() => setIsPickerOpen(true), []);
  const closePicker = useCallback(() => setIsPickerOpen(false), []);

  const shareOrders = useCallback(
    (orders: RichOrderItem[]) => {
      for (const item of orders) {
        void sendMessage({
          sessionType,
          type: ChatMessageType.Order,
          from: currentUser,
          to: targetUser,
          payload: orderItemToOrderContent(item, role),
          quoted: undefined,
        }).catch(error => console.error('share order failed:', error));
      }
    },
    [sendMessage, sessionType, currentUser, targetUser, role],
  );

  return { isPickerOpen, openPicker, closePicker, shareOrders };
}
